/**
 * The faces, as a list of checkboxes.
 *
 * **Not an alternative to picking on the canvas — the accessible path to the
 * same state.** Pointing at a face is faster for "that one" and always will be,
 * but a canvas picker is invisible to a screen reader and unreachable by
 * keyboard, and `interaction-and-devices.md` commits to every gesture having a
 * button. Pointer-only is hover-only's bigger sibling: if the only way to
 * select a face is to point at it, then for some people there is no way at all.
 *
 * It also happens to be the better shape for bulk. "Everything except the caps"
 * is four taps on the canvas and one check plus two unchecks here.
 *
 * ## One selection, one set of operations
 *
 * Nothing in this file computes a selection. Every write goes through
 * `FaceSelect.toggle` / `toggleLimb` / `selectAll` / `invertSelection` /
 * `clear`, which are the same calls the canvas makes, so both surfaces record
 * the same history entries and cannot drift. The read side is
 * `FaceSelect.subscribe`, so a face picked on the canvas re-ticks the box here
 * without this file watching anything else.
 *
 * Two pickers that disagree about what is selected would be worse than one
 * picker that is awkward, which is why the write path is a method on the picker
 * rather than `toggleFace` called from here.
 *
 * ## Why the names are the body's, not the ids'
 *
 * A list has to *name* faces, and that was the argument that nearly sank the
 * checklist: `rightLimb.left` is not the left of anything a person means — it
 * is the side pressed against the torso, 0.0026 studs from `torso.right` and
 * hidden behind it. `faceLabel` calls it Inner, so the list surfaces the body
 * rather than the template's coordinate accident.
 */

import type { RegionId } from "../../shared/format";
import {
  describe,
  FACE_GROUPS,
  type FaceGroup,
  faceLabel,
  facesOf,
  hasWholeGroup,
  type Selection,
  selectOnly,
} from "../../shared/selection";
import type { FaceSelect } from "./face-select";
import { attachPopover } from "./popover";

export interface FaceListOptions {
  faces: FaceSelect;
  /** Which garment, for the strip names: a sleeve on a shirt, a leg on pants. */
  garment: () => "shirt" | "pants";
  doc?: Document;
}

const STYLE_ID = "face-list-css";

const CSS = `
/*
  Three strips expanded is twenty-one rows and about 850px, taller than a laptop
  window with its chrome taken off. The cap itself is set by place(), which is
  the only thing that knows how much room is left; this just makes the overflow
  scrollable rather than clipped, and keeps that scroll off the page behind it.
*/
.fl { inline-size: 232px; padding: var(--space-sm); overflow-y: auto; overscroll-behavior: contain; }
.fl-title { margin: 0 0 var(--space-xs); font-size: 13px; font-weight: 600; opacity: 0.75; }
.fl-group { border-block-start: 1px solid var(--hairline, rgba(255,255,255,0.08)); }
.fl-group:first-of-type { border-block-start: 0; }
.fl-row {
  display: flex; align-items: center; gap: 8px;
  /* 44px, because this is the surface that has to work when the canvas does not. */
  min-block-size: 44px; padding-inline: 4px;
  font-size: 14px; cursor: pointer;
}
.fl-row input { inline-size: 16px; block-size: 16px; flex: none; cursor: pointer; }
.fl-name { flex: 1 1 auto; }
.fl-count { opacity: 0.55; font-variant-numeric: tabular-nums; font-size: 12px; }
.fl-expand {
  flex: none; inline-size: 32px; block-size: 32px; display: grid; place-items: center;
  border: 0; background: none; color: inherit; cursor: pointer; border-radius: 6px;
}
.fl-expand:hover { background: rgba(255,255,255,0.08); }
.fl-expand svg { inline-size: 14px; block-size: 14px; transition: transform var(--motion-fast, 120ms); }
.fl-expand[aria-expanded="true"] svg { transform: rotate(90deg); }
.fl-faces { display: none; padding-inline-start: 26px; padding-block-end: 4px; }
.fl-faces[data-open] { display: block; }
.fl-faces .fl-row { min-block-size: 34px; font-size: 13px; }
.fl-row:focus-within { outline: 2px solid var(--focus, #6ea8ff); outline-offset: -2px; border-radius: 6px; }
`;

const CHEVRON =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
  '<path d="M9 6l6 6-6 6"/></svg>';

/**
 * Name an expander after the strip it opens.
 *
 * The chevron is the only content, so without this every expander announced as
 * a bare "button" — and there is one per strip, which makes the list a row of
 * identical unnamed controls. `aria-expanded` says whether it is open but never
 * what it opens.
 *
 * Called wherever `aria-expanded` moves, rather than set once: the strip's name
 * is derived from the selection and changes underneath it.
 */
function labelExpander(expand: HTMLButtonElement, stripName: string) {
  const open = expand.getAttribute("aria-expanded") === "true";
  const label = `${open ? "Hide" : "Show"} the faces in ${stripName || "this strip"}`;
  expand.setAttribute("aria-label", label);
  expand.title = label;
}

export function mountFaceList(opts: FaceListOptions) {
  const doc = opts.doc ?? document;
  if (!doc.getElementById(STYLE_ID)) {
    const style = doc.createElement("style");
    style.id = STYLE_ID;
    style.textContent = CSS;
    doc.head.appendChild(style);
  }

  const root = doc.createElement("div");
  root.className = "fl popover floating";
  root.setAttribute("role", "dialog");
  root.setAttribute("aria-label", "Faces to paint on");

  const title = doc.createElement("h3");
  title.className = "fl-title";
  title.textContent = "Paint on";
  root.appendChild(title);

  interface GroupUI {
    group: FaceGroup;
    box: HTMLInputElement;
    name: HTMLElement;
    count: HTMLElement;
    expand: HTMLButtonElement;
    faces: HTMLElement;
    boxes: Map<RegionId, HTMLInputElement>;
  }

  const ui: GroupUI[] = FACE_GROUPS.map((group, i) => {
    const wrap = doc.createElement("div");
    wrap.className = "fl-group";

    const row = doc.createElement("label");
    row.className = "fl-row";
    const box = doc.createElement("input");
    box.type = "checkbox";
    const name = doc.createElement("span");
    name.className = "fl-name";
    const count = doc.createElement("span");
    count.className = "fl-count";
    row.appendChild(box);
    row.appendChild(name);
    row.appendChild(count);

    /*
      The expander is a sibling of the label, not inside it.

      A button inside a <label> is activated by the label's own click handling,
      so expanding a strip would also tick it — one press doing two unrelated
      things, and the checkbox appearing to toggle at random.
    */
    const expand = doc.createElement("button");
    expand.type = "button";
    expand.className = "fl-expand";
    expand.innerHTML = CHEVRON;
    expand.setAttribute("aria-expanded", "false");

    const faces = doc.createElement("div");
    faces.className = "fl-faces";
    faces.setAttribute("role", "group");
    faces.id = `fl-faces-${i}`;
    expand.setAttribute("aria-controls", faces.id);

    const holder = doc.createElement("div");
    holder.className = "fl-row";
    holder.style.padding = "0";
    holder.style.minBlockSize = "0";
    holder.appendChild(row);
    holder.appendChild(expand);
    // The label fills the row so the whole strip name is a hit target, and the
    // expander keeps its own 32px square beside it.
    row.style.flex = "1 1 auto";

    const boxes = new Map<RegionId, HTMLInputElement>();
    for (const id of facesOf(group)) {
      const fr = doc.createElement("label");
      fr.className = "fl-row";
      const fb = doc.createElement("input");
      fb.type = "checkbox";
      const fn = doc.createElement("span");
      fn.className = "fl-name";
      fn.textContent = faceLabel(id);
      fr.appendChild(fb);
      fr.appendChild(fn);
      faces.appendChild(fr);
      boxes.set(id, fb);
      fb.addEventListener("change", () => opts.faces.toggle(id));
    }

    box.addEventListener("change", () => opts.faces.toggleLimb(group));
    expand.addEventListener("click", () => {
      const open = expand.getAttribute("aria-expanded") === "true";
      expand.setAttribute("aria-expanded", String(!open));
      if (open) faces.removeAttribute("data-open");
      else faces.setAttribute("data-open", "");
      labelExpander(expand, name.textContent ?? "");
      // Expanding changes the panel's height, and its height is what decides
      // where it can sit. Placing only on open left it hanging off the bottom
      // of the window the moment a second strip was opened.
      place();
    });

    wrap.appendChild(holder);
    wrap.appendChild(faces);
    root.appendChild(wrap);
    return { group, box, name, count, expand, faces, boxes };
  });

  doc.body.appendChild(root);

  /** Re-tick everything from the one selection. The only read path. */
  function paint(selection: Selection) {
    const garment = opts.garment();
    for (const g of ui) {
      const ids = facesOf(g.group);
      const on = ids.filter((id) => selection.has(id)).length;
      const whole = hasWholeGroup(selection, g.group);
      g.box.checked = whole;
      /*
        Indeterminate is a property, never an attribute, and setting it to false
        matters as much as setting it to true — a strip that goes from partial
        to empty otherwise keeps the dash and reads as still having something in
        it.
      */
      g.box.indeterminate = on > 0 && !whole;
      g.name.textContent = describe(selectOnly(ids), garment);
      g.count.textContent = on === 0 ? "" : whole ? "all" : `${on}`;
      for (const [id, b] of g.boxes) b.checked = selection.has(id);
      // Show a strip you have picked into, so opening the panel explains the
      // selection you already have rather than hiding it behind a chevron.
      if (g.box.indeterminate && g.expand.getAttribute("aria-expanded") !== "true") {
        g.expand.setAttribute("aria-expanded", "true");
        g.faces.setAttribute("data-open", "");
      }
      // After the auto-expand above, and after the name is set: both feed it.
      labelExpander(g.expand, g.name.textContent ?? "");
    }
  }

  const unsubscribe = opts.faces.subscribe(paint);

  /** Room to leave between the panel and the window, in px. */
  const MARGIN = 8;

  /**
   * Put the panel beside its button, and never off the window.
   *
   * The cap lives here rather than in the stylesheet because this is the only
   * place that knows how much room there is. A fixed `max-block-size` would be
   * wrong on both sides — too tall on a short window, needlessly short on a
   * tall one — and it has to be applied *before* measuring, or `own.height` is
   * the height the panel wanted rather than the one it will get.
   */
  function place() {
    const r = (expandedOn ?? root).getBoundingClientRect();
    root.style.maxBlockSize = `${window.innerHeight - MARGIN * 2}px`;
    const own = root.getBoundingClientRect();
    const left = Math.min(r.right + MARGIN, window.innerWidth - own.width - MARGIN);
    const top = Math.min(Math.max(MARGIN, r.top), window.innerHeight - own.height - MARGIN);
    root.style.left = `${Math.max(MARGIN, left)}px`;
    root.style.top = `${Math.max(MARGIN, top)}px`;
  }

  /*
    A detached anchor, for the reason tool-options documents: attachPopover
    binds a click-toggle to its anchor, and the tool button already has a
    handler that selects the tool. Both would fire on one click, the panel
    would toggle twice, and it would look like it never opened.
  */
  const ghost = doc.createElement("span");
  let expandedOn: HTMLElement | null = null;
  /*
    Escape must land on the button, not on the ghost.

    attachPopover focuses its anchor on Escape — the one case where moving
    focus is right — but the anchor here is a node nobody can see or focus, so
    focus would fall to <body> and a keyboard user would be dumped at the top of
    the page. Forwarding the ghost's focus() sends it where the user came from
    without this file racing attachPopover's own handler.
  */
  ghost.focus = () => expandedOn?.focus();

  const popover = attachPopover({
    anchor: ghost,
    panel: root,
    alsoInside: () => expandedOn,
    place,
    onOpen() {
      paint(opts.faces.selection());
      /*
        Focus goes in, which popovers here normally do not do.

        The colour picker is opened by pointer and stealing focus from the
        canvas would be rude. This panel is the keyboard path by definition, and
        it is appended to <body> — so without this, tabbing from the tool button
        walks the entire rest of the app before arriving, and "reachable by
        keyboard" would be true only in the letter.
      */
      ui[0]?.box.focus();
    },
    onClose() {
      expandedOn?.setAttribute("aria-expanded", "false");
      expandedOn = null;
    },
  });

  return {
    ...popover,
    /** Show the panel against the faces tool button; a second press closes it. */
    openAt(el: HTMLElement) {
      if (popover.isOpen && expandedOn === el) {
        popover.close();
        return;
      }
      expandedOn?.setAttribute("aria-expanded", "false");
      expandedOn = el;
      el.setAttribute("aria-expanded", "true");
      el.setAttribute("aria-haspopup", "dialog");
      popover.open();
    },
    destroy() {
      unsubscribe();
      popover.destroy();
      root.remove();
    },
  };
}
